import { supabase } from './supabaseClient';

/**
 * Schon-versendet-Check, bevor der E-Mail-Dialog für einen Bericht geöffnet wird.
 *
 * Warum: Nach dem Versand läuft eine lange Persistenz-Phase (Foto-Upload,
 * Archiv-Snapshot). Wirkt sie abgebrochen, fehlt die Erfolgsmeldung, der Nutzer
 * hält den Versand für gescheitert, legt über "Begehung anlegen" eine NEUE
 * Nummer an und verschickt denselben Inhalt ein zweites Mal. Das lokale
 * isExported-Flag schützt davor nicht: die Nummernerhöhung setzt es zurück.
 *
 * Deshalb wird serverseitig nachgesehen — und zwar auf zwei Wegen:
 *   1. gleiche Berichtsnummer: Sendebericht (email_logs) oder Archiv-Eintrag
 *      (report_history) vorhanden;
 *   2. gleicher BEGEHUNGSTAG + gleicher Abschnitt unter ANDERER Nummer bereits
 *      versendet — genau das Muster des Doppelversands nach Nummernerhöhung.
 *      Ein Archiv-Eintrag allein reicht dafür bewusst nicht: beim Anlegen einer
 *      Folgebegehung wird der Vorgängerbericht ebenfalls archiviert, ohne je
 *      versendet worden zu sein. Erst ein erfolgreicher Sendebericht zu jener
 *      Nummer macht den Treffer eindeutig.
 *
 * Sonderfall zu Weg 1: Trägt der Archiv-Eintrag ein ANDERES Begehungsdatum als
 * der zu sendende Bericht, ist es sehr wahrscheinlich ein Altbestand (z. B.
 * vertippte Nummer, Vorfall Projekt 6086). Dann wird nur informativ gewarnt
 * ('archive_mismatch') und der Versand als Erstversand behandelt — sonst würde
 * ein legitimer Erstversand im Sendeverlauf fälschlich als Wiederholung geführt.
 *
 * Die Entscheidungslogik ist pur (evaluateSendGuard) und ohne DOM/Supabase
 * testbar; nur fetchPriorSendEvidence spricht mit der Datenbank.
 */

export interface SendGuardEmailLogRow {
  subject: string;
  status: string;
  sent_at: string;
}

export interface SendGuardHistoryRow {
  inspection_number: number;
  section_id?: string | null;
  date?: string | null;
}

export interface PriorSendEvidence {
  emailLogs: SendGuardEmailLogRow[];
  history: SendGuardHistoryRow[];
  /**
   * Abfrage fehlgeschlagen (offline, RLS, Timeout). Dann wird NICHT gewarnt:
   * ein Netzfehler darf einen legitimen Erstversand nicht ausbremsen.
   */
  lookupFailed?: boolean;
}

export interface SendGuardQuery {
  inspectionNumber: number;
  sectionId?: string | null;
  /** Begehungstag des Berichts (YYYY-MM-DD oder ISO). */
  reportDate?: string | null;
  /**
   * Übungsprojekt (`projects.ist_uebung`, Migration Nr. 100). Aus einem
   * Übungsprojekt geht nie eine E-Mail hinaus — siehe den ersten Fall in
   * `evaluateSendGuard`.
   */
  istUebung?: boolean;
}

export type SendGuardReason = 'none' | 'uebungsprojekt' | 'email_log' | 'report_history' | 'archive_mismatch' | 'same_day';

export interface SendGuardDecision {
  alreadySent: boolean;
  reason: SendGuardReason;
  /** Zeitpunkt des Treffers (ISO), soweit bekannt. */
  sentAt: string | null;
  /** Fertiger deutscher Warntext für den Dialog; null, wenn kein Treffer. */
  warning: string | null;
  /**
   * Der Treffer ist mutmaßlich ein Altbestand (Archiv-Eintrag mit abweichendem
   * Begehungsdatum): Dialog zeigen, aber den Versand als Erstversand behandeln —
   * kein Wiederholungs-Vermerk im Sendeverlauf.
   */
  suppressRepeatPrefix?: boolean;
  /**
   * KEIN „trotzdem senden". Der Treffer ist kein Verdacht, den der Anwender
   * abwägen könnte, sondern eine Regel — bisher gibt es genau einen solchen
   * Fall: das Übungsprojekt.
   */
  hartBlockiert?: boolean;
}

const NO_HIT: SendGuardDecision = { alreadySent: false, reason: 'none', sentAt: null, warning: null };

/**
 * Der erste Fall, und er steht bewusst VOR allem anderen — auch vor
 * `lookupFailed`.
 *
 * Ein Übungsprojekt versendet nichts. Nicht „vermutlich schon versendet",
 * nicht „bitte vorher prüfen", sondern gar nicht: die Empfänger einer Vorlage
 * sind erfundene Adressen, und ein Versand aus der Übung heraus wäre der eine
 * Fehler, der nach außen sichtbar würde. Die Datenbank hält dieselbe Regel ein
 * zweites Mal (Trigger `email_logs_kein_uebungsversand`, Nr. 101); dieser
 * Riegel ist der, der dem Anwender eine Meldung gibt statt eines Fehlers.
 */
export const UEBUNG_BLOCK: SendGuardDecision = {
  alreadySent: true,
  reason: 'uebungsprojekt',
  sentAt: null,
  warning: 'Übungsprojekt: kein Versand',
  hartBlockiert: true,
};

/** Datums-Vergleich auf Tagesebene: 'YYYY-MM-DD' aus Date- wie ISO-Strings. */
const toYmd = (value?: string | null): string | null => {
  if (!value) return null;
  const ymd = String(value).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(ymd) ? ymd : null;
};

const normalizeSection = (value?: string | null): string | null => value || null;

const formatDate = (value?: string | null): string => {
  const d = value ? new Date(value) : null;
  if (!d || isNaN(d.getTime())) return 'unbekanntem Datum';
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatDateTime = (value?: string | null): string => {
  const d = value ? new Date(value) : null;
  if (!d || isNaN(d.getTime())) return 'unbekanntem Zeitpunkt';
  const date = d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const time = d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  return `${date} um ${time}`;
};

/**
 * Berichtsnummer aus dem Mail-Betreff. Die Sendeberichte (email_logs) führen
 * keine eigene Nummernspalte; die Betreff-Konvention "… Nr. 7 …" / "… No. 7 …"
 * ist die einzige Verknüpfung — dieselbe, die auch der Sendeverlauf auswertet.
 */
export const extractInspectionNumber = (subject?: string | null): number | null => {
  if (!subject) return null;
  const match = subject.match(/\b(?:Nr|No)\.?\s*(\d+)/i);
  if (!match) return null;
  const parsed = parseInt(match[1], 10);
  return Number.isFinite(parsed) ? parsed : null;
};

/** Jüngster erfolgreicher Sendebericht zu einer Berichtsnummer. */
const findSuccessfulSend = (
  logs: SendGuardEmailLogRow[],
  inspectionNumber: number,
): SendGuardEmailLogRow | null => {
  const hits = logs
    .filter(l => l.status === 'success' && extractInspectionNumber(l.subject) === inspectionNumber)
    .sort((a, b) => new Date(b.sent_at).getTime() - new Date(a.sent_at).getTime());
  return hits[0] || null;
};

/**
 * Ein Sendebericht, der deutlich VOR dem Begehungstag liegt, kann diesen
 * Bericht nicht enthalten haben — er gehört zu einem älteren Bericht gleicher
 * Nummer (Vorfall 6214: die App hatte das Projekt „Kreetsander" mit der
 * Entwässerungsanlage überschrieben; die Sendeberichte Nr. 27–37 der alten
 * Baustelle blieben unter derselben Projekt-ID und stempelten jeden Erstversand
 * der neuen als Wiederholung). Solche Zeilen sind kein Beleg und werden vor
 * jeder Regel ausgeblendet.
 *
 * Toleranz ein Tag: sent_at ist UTC, der Begehungstag ist der Ortstag. Erst ein
 * Versand, der mindestens zwei Tage vor dem Begehungstag liegt, gilt als alt.
 */
const isStaleForReport = (sentAt: string, reportDay: string | null): boolean => {
  if (!reportDay) return false;
  const sent = new Date(sentAt).getTime();
  const dayStart = new Date(`${reportDay}T00:00:00Z`).getTime();
  if (isNaN(sent) || isNaN(dayStart)) return false;
  return sent < dayStart - 24 * 60 * 60 * 1000;
};

export const evaluateSendGuard = (query: SendGuardQuery, evidence: PriorSendEvidence): SendGuardDecision => {
  // ERSTER FALL: Übungsprojekt. Siehe UEBUNG_BLOCK.
  if (query.istUebung) return UEBUNG_BLOCK;

  if (evidence.lookupFailed) return NO_HIT;

  const { inspectionNumber } = query;
  const section = normalizeSection(query.sectionId);
  const queryDayForLogs = toYmd(query.reportDate);
  const logs = (evidence.emailLogs || []).filter(l => !isStaleForReport(l.sent_at, queryDayForLogs));
  const history = evidence.history || [];

  // 1. Exakt dieselbe Nummer wurde schon erfolgreich verschickt.
  const directSend = findSuccessfulSend(logs, inspectionNumber);
  if (directSend) {
    return {
      alreadySent: true,
      reason: 'email_log',
      sentAt: directSend.sent_at,
      warning: `Bericht Nr. ${inspectionNumber} wurde bereits am ${formatDateTime(directSend.sent_at)} versendet.`,
    };
  }

  // 2. Exakt dieselbe Nummer liegt bereits im Archiv (Snapshot entsteht beim
  //    Versand). Ohne Sendebericht ist das nur ein Indiz — z. B. bei lokalem
  //    Outlook-Versand, der nicht protokolliert wird.
  const archived = history.find(
    h => h.inspection_number === inspectionNumber && normalizeSection(h.section_id) === section,
  );
  if (archived) {
    // Abweichendes Begehungsdatum: mutmaßlich Altbestand (z. B. vertippte
    // Nummer) — informieren, aber den Erstversand nicht als Wiederholung
    // stempeln. Ohne beide Daten kein Downgrade: dann bleibt die volle Warnung.
    const archivedDay = toYmd(archived.date);
    const queryDay = toYmd(query.reportDate);
    if (archivedDay && queryDay && archivedDay !== queryDay) {
      return {
        alreadySent: true,
        reason: 'archive_mismatch',
        sentAt: archived.date || null,
        warning: `Im Archiv liegt ein Eintrag zu Bericht Nr. ${inspectionNumber} mit abweichendem Begehungsdatum (${formatDate(archived.date)}) — vermutlich ein Altbestand (z. B. vertippte Nummer). Ein Versand von Bericht Nr. ${inspectionNumber} ist nicht protokolliert; dieser Versand wird als Erstversand behandelt.`,
        suppressRepeatPrefix: true,
      };
    }
    return {
      alreadySent: true,
      reason: 'report_history',
      sentAt: archived.date || null,
      warning: `Bericht Nr. ${inspectionNumber} (Begehung vom ${formatDate(archived.date)}) liegt bereits im Archiv — er wurde also schon einmal abgeschlossen.`,
    };
  }

  // 3. Andere Nummer, aber gleicher Begehungstag und Abschnitt, und zu jener
  //    Nummer existiert ein erfolgreicher Sendebericht: das ist der
  //    Doppelversand nach Nummernerhöhung.
  const reportDay = toYmd(query.reportDate);
  if (reportDay) {
    const sameDay = history
      .filter(h =>
        h.inspection_number !== inspectionNumber &&
        normalizeSection(h.section_id) === section &&
        toYmd(h.date) === reportDay,
      )
      .map(h => ({ row: h, log: findSuccessfulSend(logs, h.inspection_number) }))
      .filter(x => x.log)
      .sort((a, b) => new Date(b.log!.sent_at).getTime() - new Date(a.log!.sent_at).getTime());

    const hit = sameDay[0];
    if (hit) {
      return {
        alreadySent: true,
        reason: 'same_day',
        sentAt: hit.log!.sent_at,
        warning: `Für die Begehung am ${formatDate(reportDay)} wurde bereits Bericht Nr. ${hit.row.inspection_number} versendet (am ${formatDateTime(hit.log!.sent_at)}). Dieser Bericht Nr. ${inspectionNumber} betrifft denselben Begehungstag.`,
      };
    }
  }

  return NO_HIT;
};

/** Liest Sendeberichte und Archiv-Einträge des Projekts (schmale Spaltenauswahl). */
export const fetchPriorSendEvidence = async (projectId: string): Promise<PriorSendEvidence> => {
  try {
    const [logsRes, historyRes] = await Promise.all([
      supabase
        .from('email_logs')
        .select('subject, status, sent_at')
        .eq('project_id', projectId)
        .eq('status', 'success')
        .order('sent_at', { ascending: false })
        .limit(200),
      supabase
        .from('report_history')
        .select('inspection_number, section_id, date')
        .eq('project_id', projectId),
    ]);

    if (logsRes.error) throw logsRes.error;
    if (historyRes.error) throw historyRes.error;

    return { emailLogs: logsRes.data || [], history: historyRes.data || [] };
  } catch (e) {
    console.warn('Schon-versendet-Prüfung fehlgeschlagen — Versand wird nicht blockiert:', e);
    return { emailLogs: [], history: [], lookupFailed: true };
  }
};

/** Komfort-Einstieg für die UI: Abfrage + Bewertung. */
export const checkAlreadySent = async (
  projectId: string | null | undefined,
  query: SendGuardQuery,
): Promise<SendGuardDecision> => {
  // Vor der Abfrage, nicht danach: ein Übungsprojekt braucht keinen Beleg,
  // und ohne Projekt-id (temp-id) gilt die Regel genauso.
  if (query.istUebung) return UEBUNG_BLOCK;
  if (!projectId || projectId === 'temp-id') return NO_HIT;
  return evaluateSendGuard(query, await fetchPriorSendEvidence(projectId));
};
